import { audio } from '../../../../audio/AudioManager.js';
import { SaveManager } from '../../../../state/SaveManager.js';
import { applicationLifecycle } from '../../../shared/lifecycle/index.js';
import { ensureMinimumTextTouchTarget } from '../../../ui/TouchTarget.js';
import { resolveStoryForestLayout, STORY_FOREST_LAYOUT_HEIGHT, STORY_FOREST_LAYOUT_WIDTH } from '../components/StoryForestLayout.js';
import { STORY_FOREST_ASSETS } from '../StoryForestAssets.js';
import {
  STORY_FOREST_DISTRICTS,
  completeStoryForestDistrictProgress,
  getStoryForestState,
  saveStoryForestDistrictProgress,
  visitStoryForestDistrict,
} from '../StoryForestState.js';
import { CHARACTER_GROVE_ASSETS } from '../minigames/character-grove/CharacterGroveAssets.js';
import {
  SEQUENCE_STREAM_ASSETS,
  SEQUENCE_STREAM_ASSET_LIST,
  canEnterSequenceStream,
  getMissingSequenceStreamAssets,
} from './SequenceStreamAssets.js';
import { SEQUENCE_STREAM_STORY } from './SequenceStreamConfig.js';
import { SequenceStreamState, type SequenceStreamSerializedState } from './SequenceStreamState.js';
import { canEnterMainIdeaMeadow } from '../main-idea-meadow/MainIdeaMeadowAssets.js';

export const SEQUENCE_STREAM_SCENE_KEY = 'SequenceStream';
const FONT_READING = '"Nunito", "Segoe UI", Arial, sans-serif';

interface SequenceStreamProgress extends SequenceStreamSerializedState {
  readonly restored: boolean;
}

interface CardView {
  readonly id?: string;
  readonly image: Phaser.GameObjects.Image;
  readonly label: Phaser.GameObjects.Text;
  readonly badge: Phaser.GameObjects.Text;
}

interface ArtButton {
  readonly image: Phaser.GameObjects.Image;
  readonly label: Phaser.GameObjects.Text;
}

export class SequenceStreamScene extends Phaser.Scene {
  static readonly requiredImageKeys = SEQUENCE_STREAM_ASSET_LIST.map(asset => asset.key);

  private state!: SequenceStreamState;
  private dormantBackground!: Phaser.GameObjects.Image;
  private restoredBackground!: Phaser.GameObjects.Image;
  private missionPanel!: Phaser.GameObjects.Image;
  private cosmo!: Phaser.GameObjects.Image;
  private titleText!: Phaser.GameObjects.Text;
  private passageText!: Phaser.GameObjects.Text;
  private instructionText!: Phaser.GameObjects.Text;
  private feedbackText!: Phaser.GameObjects.Text;
  private controlsText!: Phaser.GameObjects.Text;
  private backText!: Phaser.GameObjects.Text;
  private homeText!: Phaser.GameObjects.Text;
  private nextText!: Phaser.GameObjects.Text;
  private sourceCards: CardView[] = [];
  private slotCards: CardView[] = [];
  private buttons: ArtButton[] = [];
  private restored = false;
  private leaving = false;
  private suspended = false;
  private reducedMotion = false;
  private renderScale = 1;
  private resizeTimer?: number;
  private unsubscribeLifecycle?: () => void;

  constructor() { super({ key: SEQUENCE_STREAM_SCENE_KEY }); }

  create() {
    this.sourceCards = [];
    this.slotCards = [];
    this.buttons = [];
    this.leaving = false;
    this.suspended = false;

    const world = getStoryForestState();
    const missing = getMissingSequenceStreamAssets(this.textures);
    if (!world.unlockedDistricts.includes(STORY_FOREST_DISTRICTS.sequenceStream)
      || !canEnterSequenceStream(this.textures)) {
      if (missing.length > 0) {
        console.error('[SequenceStream] Required production textures are missing:',
          missing.map(asset => `${asset.key} (${asset.path})`));
      }
      this.scene.start('CharacterGrove');
      return;
    }

    void audio.enterSoundscape('story-forest');
    this.renderScale = this.configureRenderer();
    this.reducedMotion = SaveManager.getSettings().reducedMotion
      || (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
    this.restored = world.completedDistricts.includes(STORY_FOREST_DISTRICTS.sequenceStream);
    this.restoreProgress(world.districtProgress[STORY_FOREST_DISTRICTS.sequenceStream]);
    visitStoryForestDistrict(STORY_FOREST_DISTRICTS.sequenceStream);
    SaveManager.setLastScene(SEQUENCE_STREAM_SCENE_KEY);
    SaveManager.save();
    this.buildEnvironment();
    this.buildInterface();
    this.registerInput();
    this.registerLifecycle();
    this.refresh();
    this.applyTextResolution();
    this.cameras.main.fadeIn(this.reducedMotion ? 150 : 440, 84, 122, 78);
  }

  private restoreProgress(value: unknown) {
    const progress = value as Partial<SequenceStreamProgress> | undefined;
    if (progress?.version === 1) {
      this.state = new SequenceStreamState(SEQUENCE_STREAM_STORY, progress);
      // A completed district may be actively replayed; that unfinished replay is
      // allowed to resume without removing the durable mastery record.
      this.restored = progress.restored === true;
      return;
    }
    this.state = new SequenceStreamState(SEQUENCE_STREAM_STORY);
  }

  private buildEnvironment() {
    this.dormantBackground = this.add.image(640, 360, SEQUENCE_STREAM_ASSETS.dormant.key)
      .setDisplaySize(1280, 720).setDepth(0);
    this.restoredBackground = this.add.image(640, 360, SEQUENCE_STREAM_ASSETS.restored.key)
      .setDisplaySize(1280, 720).setDepth(1);
    this.missionPanel = this.add.image(640, 108, STORY_FOREST_ASSETS.storyMissionPanel.key).setDepth(30);
    this.cosmo = this.add.image(100, 650, STORY_FOREST_ASSETS.storyForestCosmo.key).setDepth(105);
  }

  private buildInterface() {
    this.titleText = this.makeText('SEQUENCE STREAM', 29, '#fff2b8', 'bold')
      .setStroke('#173f2b', 7).setDepth(110);
    this.passageText = this.makeText(SEQUENCE_STREAM_STORY.passage, 16, '#3e3028', 'normal')
      .setWordWrapWidth(760).setLineSpacing(4).setDepth(40);
    this.instructionText = this.makeText('Select a story moment, then place it FIRST, NEXT, or LAST.', 15, '#fff8dc', 'bold')
      .setStroke('#173f2b', 5).setDepth(110);
    this.feedbackText = this.makeText('', 14, '#fff8dc', 'bold')
      .setStroke('#173f2b', 5).setWordWrapWidth(760).setDepth(110);
    this.controlsText = this.makeText('1–3 select • Q/W/E place • Enter check • H hint • R reset • Esc grove • M Home', 10, '#fff8dc', 'bold')
      .setStroke('#173f2b', 5).setDepth(110);
    this.backText = this.makeText('←  CHARACTER GROVE', 14, '#ffffff', 'bold')
      .setStroke('#173f2b', 6).setInteractive({ useHandCursor: true }).setDepth(112);
    this.homeText = this.makeText('HOME HUB  (M)', 14, '#ffffff', 'bold')
      .setStroke('#173f2b', 6).setInteractive({ useHandCursor: true }).setDepth(112);
    this.nextText = this.makeText('MAIN IDEA MEADOW • NEXT PATH', 15, '#fff2b8', 'bold')
      .setStroke('#173f2b', 6).setInteractive({ useHandCursor: true }).setDepth(112);
    [this.backText, this.homeText, this.nextText].forEach(target => ensureMinimumTextTouchTarget(target));
    this.backText.on('pointerup', this.returnToGrove, this);
    this.homeText.on('pointerup', this.returnHome, this);
    this.nextText.on('pointerup', this.tryNextDistrict, this);

    this.sourceCards = SEQUENCE_STREAM_STORY.events.map((event, index) => {
      const image = this.add.image(0, 0, SEQUENCE_STREAM_ASSETS.eventPage.key)
        .setDepth(60).setInteractive({ useHandCursor: true });
      const label = this.makeText(event.text, 13, '#3e3028', 'bold')
        .setWordWrapWidth(160).setDepth(65);
      const badge = this.makeText(`${index + 1}`, 13, '#fff8dc', 'bold')
        .setStroke('#173f2b', 4).setDepth(66);
      image.on('pointerup', () => this.selectEvent(event.id));
      return { id: event.id, image, label, badge };
    });
    this.slotCards = SEQUENCE_STREAM_STORY.events.map((_event, index) => {
      const image = this.add.image(0, 0, SEQUENCE_STREAM_ASSETS.eventPage.key)
        .setDepth(70).setInteractive({ useHandCursor: true });
      const label = this.makeText('', 13, '#3e3028', 'bold')
        .setWordWrapWidth(160).setDepth(75);
      const badge = this.makeText(index === 0 ? 'FIRST' : index === 1 ? 'NEXT' : 'LAST', 12, '#fff8dc', 'bold')
        .setStroke('#173f2b', 4).setDepth(76);
      image.on('pointerup', () => this.activateSlot(index));
      return { image, label, badge };
    });
    this.buttons = [
      this.makeButton('CHECK', () => this.check()),
      this.makeButton('HINT', () => this.hint()),
      this.makeButton('RESET', () => this.reset()),
    ];
  }

  private makeText(value: string, size: number, color: string, style: 'normal' | 'bold') {
    return this.add.text(0, 0, value, {
      fontFamily: FONT_READING, fontSize: `${size}px`, fontStyle: style,
      color, align: 'center', lineSpacing: 3,
    }).setOrigin(0.5);
  }

  private makeButton(label: string, action: () => void): ArtButton {
    const image = this.add.image(0, 0, CHARACTER_GROVE_ASSETS.connectButton.key)
      .setDepth(100).setInteractive({ useHandCursor: true });
    const text = this.makeText(label, 13, '#fff8dc', 'bold').setStroke('#173f2b', 4).setDepth(101);
    image.on('pointerup', action);
    return { image, label: text };
  }

  private selectEvent(eventId: string) {
    if (!this.canAct() || !this.state.select(eventId)) return;
    this.feedbackText.setText(this.state.snapshot.selectedEventId
      ? 'Story moment selected. Choose FIRST, NEXT, or LAST.'
      : 'Selection cleared. Choose any unplaced story moment.');
    audio.click();
    this.capture();
  }

  private activateSlot(index: number) {
    if (!this.canAct() || !this.state.activateSlot(index)) return;
    this.feedbackText.setText(this.state.snapshot.selectedEventId
      ? 'That moment is back in your hand. Choose a new place for it.'
      : 'The moment settles onto the stream. You can move it again before checking.');
    audio.click();
    this.capture();
  }

  private check() {
    if (this.restored) return this.replay();
    if (!this.canAct()) return;
    const result = this.state.check();
    if (result === 'incomplete') {
      this.feedbackText.setText('Every story moment needs a place before the stream can connect.');
      audio.wrong();
      return this.capture();
    }
    if (result === 'incorrect') {
      SaveManager.recordAnswer(SEQUENCE_STREAM_STORY.skillId, false);
      this.feedbackText.setText('The stream is still tangled. Reread what happened first and what caused the lantern to light.');
      audio.wrong();
      return this.capture();
    }
    SaveManager.recordAnswer(SEQUENCE_STREAM_STORY.skillId, true);
    this.restored = true;
    completeStoryForestDistrictProgress(
      STORY_FOREST_DISTRICTS.sequenceStream,
      this.progressSnapshot(),
      SEQUENCE_STREAM_STORY.rewardEvent,
      STORY_FOREST_DISTRICTS.mainIdeaMeadow,
    );
    this.feedbackText.setText(SEQUENCE_STREAM_STORY.successMessage);
    this.playRestoration();
    audio.restoration();
    this.capture();
  }

  private hint() {
    if (!this.canAct()) return;
    this.feedbackText.setText(this.state.requestHint());
    audio.hint();
    this.capture();
  }

  private reset() {
    if (!this.canAct() || !this.state.reset()) return;
    this.feedbackText.setText('The three moments return to the bank. Your completed forest paths stay safe.');
    audio.click();
    this.capture();
  }

  private replay() {
    this.restored = false;
    this.state = new SequenceStreamState(SEQUENCE_STREAM_STORY);
    this.feedbackText.setText('Replay mode: reconnect the story without changing saved mastery.');
    audio.click();
    this.capture();
  }

  private capture() {
    saveStoryForestDistrictProgress(STORY_FOREST_DISTRICTS.sequenceStream, this.progressSnapshot());
    this.refresh();
  }

  private progressSnapshot(): SequenceStreamProgress {
    return { ...this.state.serialize(), restored: this.restored };
  }

  private refresh() {
    const snapshot = this.state.snapshot;
    this.restoredBackground.setAlpha(this.restored ? 1 : 0);
    this.dormantBackground.setAlpha(this.restored ? 0 : 1);
    this.sourceCards.forEach(view => {
      const available = snapshot.availableEventIds.includes(view.id ?? '');
      const selected = snapshot.selectedEventId === view.id;
      view.image.setVisible(!this.restored && available)
        .setAlpha(selected ? 1 : 0.88).setScale(selected ? 1.06 : 1);
      view.label.setVisible(!this.restored && available)
        .setText(`${this.state.eventForId(view.id ?? null)?.text ?? ''}${selected ? '\nSELECTED' : ''}`);
      view.badge.setVisible(!this.restored && available);
    });
    this.slotCards.forEach((view, index) => {
      const event = this.state.eventForId(snapshot.placedOrder[index]);
      view.image.setVisible(!this.restored).setAlpha(event ? 1 : 0.7);
      view.label.setVisible(!this.restored).setText(event?.text ?? 'Choose a moment');
      view.badge.setVisible(!this.restored);
    });
    this.buttons.forEach((button, index) => {
      const visible = !this.restored || index === 0;
      button.image.setVisible(visible);
      button.label.setVisible(visible);
    });
    this.buttons[0].label.setText(this.restored ? 'REPLAY' : 'CHECK');
    this.instructionText.setVisible(!this.restored);
    this.controlsText.setVisible(!this.restored);
    this.nextText.setVisible(this.restored);
    if (this.restored) this.passageText.setText('The three story moments connect. The lit stream now leads toward Main Idea Meadow.');
    else this.passageText.setText(SEQUENCE_STREAM_STORY.passage);
    if (!this.feedbackText.text) this.feedbackText.setText('Read the whole passage, then rebuild its cause-and-effect sequence.');
    this.layout();
  }

  private layout() {
    const { safe, compact } = resolveStoryForestLayout(this);
    const portrait = safe.width < 700;
    const panelWidth = portrait ? safe.width - 24 : Math.min(compact ? 780 : 900, safe.width - 190);
    this.missionPanel.setPosition(safe.centerX, safe.y + (portrait ? 137 : compact ? 102 : 112))
      .setDisplaySize(panelWidth, panelWidth * (1086 / 1448));
    this.titleText.setPosition(safe.centerX, safe.y + (portrait ? 51 : 28))
      .setFontSize(portrait ? 21 : compact ? 24 : 29);
    this.passageText.setPosition(safe.centerX, safe.y + (portrait ? 139 : compact ? 105 : 116))
      .setFontSize(portrait ? 12 : compact ? 14 : 16).setWordWrapWidth(panelWidth * 0.76);
    this.backText.setPosition(safe.x + (portrait ? 76 : 91), safe.y + 23)
      .setFontSize(portrait ? 8 : compact ? 11 : 14);
    this.homeText.setPosition(safe.right - (portrait ? 55 : 67), safe.y + 23)
      .setFontSize(portrait ? 9 : compact ? 11 : 14);
    this.instructionText.setPosition(safe.centerX, safe.y + (portrait ? 229 : compact ? 205 : 222))
      .setFontSize(portrait ? 11 : compact ? 13 : 15);

    const gap = portrait ? Math.min(190, (safe.width - 40) / 3) : compact ? 215 : 240;
    const cardW = portrait ? Math.min(175, gap - 8) : compact ? 190 : 210;
    const cardH = portrait ? 130 : compact ? 138 : 152;
    const sourceY = safe.y + (portrait ? 305 : compact ? 292 : 315);
    const slotY = safe.y + (portrait ? 448 : compact ? 438 : 474);
    [this.sourceCards, this.slotCards].forEach((views, row) => views.forEach((view, index) => {
      const x = safe.centerX + (index - 1) * gap;
      const y = row === 0 ? sourceY : slotY;
      view.image.setPosition(x, y).setDisplaySize(cardW, cardH);
      view.label.setPosition(x, y + (row === 0 ? 2 : 5))
        .setFontSize(portrait ? 9 : compact ? 10 : 12).setWordWrapWidth(cardW * 0.68);
      view.badge.setPosition(x - cardW * 0.37, y - cardH * 0.35)
        .setFontSize(portrait ? 9 : 11);
    }));

    const buttonY = safe.bottom - (portrait ? 57 : 48);
    const buttonGap = portrait ? Math.min(150, (safe.width - 80) / 2) : 175;
    this.buttons.forEach((button, index) => {
      const x = safe.centerX + (index - 1) * buttonGap;
      button.image.setPosition(x, buttonY).setDisplaySize(portrait ? 125 : 145, portrait ? 56 : 64);
      button.label.setPosition(x, buttonY).setFontSize(portrait ? 10 : 13);
    });
    this.feedbackText.setPosition(
      safe.centerX,
      safe.bottom - (this.restored ? (portrait ? 162 : 148) : (portrait ? 111 : 105)),
    )
      .setFontSize(portrait ? 10 : compact ? 12 : 14)
      .setWordWrapWidth(portrait ? safe.width - 34 : Math.min(760, safe.width - 200));
    this.controlsText.setPosition(safe.centerX, safe.bottom - 14)
      .setFontSize(portrait ? 7 : compact ? 9 : 10).setWordWrapWidth(safe.width - 20);
    this.nextText.setPosition(safe.centerX, safe.bottom - (portrait ? 118 : 96))
      .setFontSize(portrait ? 12 : 15);
    this.cosmo.setPosition(safe.x + (compact ? 52 : 68), safe.bottom - (compact ? 51 : 61))
      .setDisplaySize(compact ? 75 : 92, compact ? 100 : 123);
  }

  private playRestoration() {
    const effect = this.add.image(640, 390, STORY_FOREST_ASSETS.storyMagicVfx.key)
      .setDisplaySize(520, 520).setDepth(95).setBlendMode(Phaser.BlendModes.ADD)
      .setAlpha(this.reducedMotion ? 0.7 : 0);
    if (this.reducedMotion) this.time.delayedCall(800, () => effect.destroy());
    else this.tweens.add({ targets: effect, alpha: 0.92, scaleX: 1.15, scaleY: 1.15,
      duration: 560, yoyo: true, hold: 160, onComplete: () => effect.destroy() });
  }

  private registerInput() { this.input.keyboard?.on('keydown', this.handleKeyDown, this); }

  private handleKeyDown(event: KeyboardEvent) {
    if (this.leaving || this.suspended) return;
    const key = event.key.toLocaleLowerCase();
    if (key === 'escape') return this.returnToGrove();
    if (key === 'm') return this.returnHome();
    if (key === 'enter' || key === ' ') return this.check();
    const sourceIndex = ['1', '2', '3'].indexOf(key);
    if (sourceIndex >= 0) return this.selectEvent(SEQUENCE_STREAM_STORY.events[sourceIndex].id);
    const slotIndex = ['q', 'w', 'e'].indexOf(key);
    if (slotIndex >= 0) return this.activateSlot(slotIndex);
    if (key === 'h') return this.hint();
    if (key === 'r') this.reset();
  }

  private registerLifecycle() {
    this.unsubscribeLifecycle = applicationLifecycle.subscribe({
      onSuspend: () => { this.suspended = true; },
      onResume: () => { this.suspended = false; },
    });
    window.addEventListener('resize', this.handleResize);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);
  }

  private canAct() { return !this.leaving && !this.suspended && !this.restored; }

  private configureRenderer() {
    const parentW = this.scale.parentSize.width || window.innerWidth;
    const parentH = this.scale.parentSize.height || window.innerHeight;
    const cssScale = Math.min(parentW / STORY_FOREST_LAYOUT_WIDTH, parentH / STORY_FOREST_LAYOUT_HEIGHT);
    const scale = Phaser.Math.Clamp(cssScale * Phaser.Math.Clamp(window.devicePixelRatio || 1, 1, 2), 1, 2);
    this.scale.scaleMode = Phaser.Scale.FIT;
    this.scale.autoCenter = Phaser.Scale.CENTER_BOTH;
    this.scale.displaySize.setAspectMode(Phaser.Scale.FIT);
    this.scale.setGameSize(Math.round(STORY_FOREST_LAYOUT_WIDTH * scale), Math.round(STORY_FOREST_LAYOUT_HEIGHT * scale));
    this.scale.refresh();
    this.cameras.main.setZoom(scale).centerOn(640, 360);
    return scale;
  }

  private applyTextResolution() {
    this.children.list.forEach(object => {
      if (object instanceof Phaser.GameObjects.Text) object.setResolution(this.renderScale);
    });
  }

  private readonly handleResize = () => {
    if (this.resizeTimer !== undefined) window.clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(() => {
      if (!this.scene.isActive()) return;
      this.renderScale = this.configureRenderer();
      this.layout();
      this.applyTextResolution();
    }, 80);
  };

  private readonly returnToGrove = () => {
    if (this.leaving) return;
    this.leaving = true;
    saveStoryForestDistrictProgress(STORY_FOREST_DISTRICTS.sequenceStream, this.progressSnapshot());
    this.scene.start('CharacterGrove');
  };

  private readonly returnHome = () => {
    if (this.leaving) return;
    this.leaving = true;
    saveStoryForestDistrictProgress(STORY_FOREST_DISTRICTS.sequenceStream, this.progressSnapshot());
    SaveManager.setLastScene(SEQUENCE_STREAM_SCENE_KEY);
    SaveManager.save();
    this.scene.start('HomeHub');
  };

  private readonly tryNextDistrict = () => {
    if (!this.restored) return;
    if (!canEnterMainIdeaMeadow(this.textures)) {
      this.feedbackText.setText('The meadow path is unlocked. Main Idea Meadow is awaiting its runtime QA gate.');
      return audio.click();
    }
    this.leaving = true;
    saveStoryForestDistrictProgress(STORY_FOREST_DISTRICTS.sequenceStream, this.progressSnapshot());
    this.scene.start('MainIdeaMeadow');
  };

  private shutdown() {
    if (this.state) saveStoryForestDistrictProgress(STORY_FOREST_DISTRICTS.sequenceStream, this.progressSnapshot());
    this.unsubscribeLifecycle?.();
    audio.leaveSoundscape({ id: 'story-forest' });
    window.removeEventListener('resize', this.handleResize);
    if (this.resizeTimer !== undefined) window.clearTimeout(this.resizeTimer);
    this.input.keyboard?.off('keydown', this.handleKeyDown, this);
    this.tweens.killAll();
  }
}
